class Formatters {
  /**
   * Format date to MM/DD/YYYY
   * @param {Date|string} date
   * @returns {string}
   */
  static formatDate(date) {
    if (!date) return ''
    const d = new Date(date)
    return `${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getDate().toString().padStart(2, '0')}/${d.getFullYear()}`
  }

  static formatDateForInput(date) {
    if (!date) return ''
    const d = new Date(date)
    // Return in YYYY-MM-DD format for <input type="date">
    return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`
}

  /**
   * Format number with specific decimals
   * @param {number} value
   * @param {number} decimals
   * @returns {string}
   */
  static formatNumber(value, decimals = 2) {
    if (isNaN(value)) return '-'
    return parseFloat(value).toFixed(decimals)
  }

  /**
   * Format currency with specific decimals
   * @param {number} value
   * @param {number} decimals
   * @returns {string}
   */
  static formatCurrency(value, decimals = 2) {
    const num = this.formatNumber(value, decimals)
    if(value === 0 || !value)
      return '-'
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(num).replace('$', '₱')
  }

  static formatCurrencyWithoutSymbol(value, decimals = 2) {
    const num = this.formatNumber(value, decimals)
    if(value === 0 || !value)
      return '-'
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(num).replace('$', '')
  }

  /**
   * Pad numbers with leading zeros
   * @param {number} num
   * @param {number} length
   * @returns {string}
   */
  static padNumber(num, length = 8) {
    return num.toString().padStart(length, '0')
  }

  /**
   * Convert number to words (Philippine Peso format)
   * @param {number} num - The number to convert
   * @returns {string} - Number in words (e.g., "One thousand two hundred thirty-four pesos and 56/100")
   */
  static numberToWords(num) {
    if (num === 0) return 'Zero pesos only';
    if (!num || isNaN(num)) return '';
    
    const amount = parseFloat(num);
    const whole = Math.floor(amount);
    const cents = Math.round((amount - whole) * 100);
    
    const wholeWords = this._convertToWords(whole);
    const centWords = cents > 0 ? `${cents.toString().padStart(2, '0')}/100` : '';
    
    let result = '';
    if (wholeWords) {
      result = this._capitalizeFirstLetter(wholeWords) + ' peso' + (whole !== 1 ? 's' : '');
    }
    
    if (centWords) {
      result += result ? ' and ' + centWords : centWords + ' centavos';
    } else {
      result += ' only';
    }
    
    return result;
  }

  /**
   * Private helper: Convert number to words (0-999,999,999,999)
   */
  static _convertToWords(num) {
    if (num === 0) return '';
    
    const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
    const teens = ['ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 
                   'seventeen', 'eighteen', 'nineteen'];
    const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 
                  'eighty', 'ninety'];
    const thousands = ['', 'thousand', 'million', 'billion'];
    
    if (num < 0) return 'negative ' + this._convertToWords(-num);
    
    let result = '';
    let groupIndex = 0;
    
    while (num > 0) {
      const chunk = num % 1000;
      if (chunk !== 0) {
        const chunkWords = this._convertChunk(chunk, ones, teens, tens);
        result = chunkWords + (thousands[groupIndex] ? ' ' + thousands[groupIndex] : '') + 
                 (result ? ' ' + result : '');
      }
      num = Math.floor(num / 1000);
      groupIndex++;
    }
    
    return result.trim();
  }

  /**
   * Private helper: Convert a chunk (0-999) to words
   */
  static _convertChunk(num, ones, teens, tens) {
    if (num === 0) return '';
    
    let result = '';
    const hundreds = Math.floor(num / 100);
    const remainder = num % 100;
    
    if (hundreds > 0) {
      result += ones[hundreds] + ' hundred';
      if (remainder > 0) result += ' ';
    }
    
    if (remainder > 0) {
      if (remainder < 10) {
        result += ones[remainder];
      } else if (remainder < 20) {
        result += teens[remainder - 10];
      } else {
        const ten = Math.floor(remainder / 10);
        const one = remainder % 10;
        result += tens[ten];
        if (one > 0) {
          result += '-' + ones[one];
        }
      }
    }
    
    return result;
  }

  /**
   * Private helper: Capitalize first letter
   */
  static _capitalizeFirstLetter(string) {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  /**
   * Alternative simpler numberToWords (for amounts up to millions)
   */
  static numberToWordsSimple(num) {
    if (num === 0) return 'Zero pesos only';
    if (!num || isNaN(num)) return '';
    
    const amount = parseFloat(num);
    const whole = Math.floor(amount);
    const cents = Math.round((amount - whole) * 100);
    
    const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
    const teens = ['ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 
                   'seventeen', 'eighteen', 'nineteen'];
    const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 
                  'eighty', 'ninety'];
    
    // Convert whole number part
    let wholeWords = '';
    
    if (whole < 10) {
      wholeWords = ones[whole];
    } else if (whole < 20) {
      wholeWords = teens[whole - 10];
    } else if (whole < 100) {
      const ten = Math.floor(whole / 10);
      const one = whole % 10;
      wholeWords = tens[ten] + (one > 0 ? '-' + ones[one] : '');
    } else if (whole < 1000) {
      const hundred = Math.floor(whole / 100);
      const remainder = whole % 100;
      wholeWords = ones[hundred] + ' hundred' + 
                   (remainder > 0 ? ' ' + this.numberToWordsSimple(remainder).replace(' pesos only', '') : '');
    } else if (whole < 1000000) {
      const thousand = Math.floor(whole / 1000);
      const remainder = whole % 1000;
      wholeWords = this.numberToWordsSimple(thousand).replace(' pesos only', '') + ' thousand' + 
                   (remainder > 0 ? ' ' + this.numberToWordsSimple(remainder).replace(' pesos only', '') : '');
    } else {
      return 'Very large amount';
    }
    
    // Capitalize first letter
    wholeWords = this._capitalizeFirstLetter(wholeWords);
    
    // Handle cents
    let result = '';
    if (wholeWords) {
      result = wholeWords + ' peso' + (whole !== 1 ? 's' : '');
    }
    
    if (cents > 0) {
      result += ' and ' + cents.toString().padStart(2, '0') + '/100';
    } else {
      result += ' only';
    }
    
    return result;
  }

  /**
   * Format date to readable string (e.g., "January 15, 2024")
   */
  static formatDateLong(date) {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  /**
   * Format date with time
   */
  static formatDateTime(date) {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Truncate text with ellipsis
   */
  static truncateText(text, maxLength = 50) {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  }

  /**
   * Format percentage
   */
  static formatPercentage(value, decimals = 2) {
    if (value === null || value === undefined || isNaN(value)) return '0%';
    return parseFloat(value).toFixed(decimals) + '%';
  }
}

export default Formatters